/**
 * Enums and domain constants shared by the API and the browser.
 *
 * These mirror the ENUM definitions in the MySQL schema exactly. When one changes,
 * both change together — that is the entire reason this file is shared rather than
 * duplicated on each side.
 */

/** Document types — drive the numbering series, PDF templates and Drive folders. */
export const DOC_TYPE = Object.freeze({
  INVOICE: 'invoice',
  GOLD_PURCHASE: 'gold_purchase',
  REPAIR: 'repair',
  ISSUE: 'issue',
  RECEIPT: 'receipt',
  CHIT_RECEIPT: 'chit_receipt',
  CHIT_ACCOUNT: 'chit_account',
  REPORT: 'report',
});

/** Numbering prefixes. Editable per financial year in Settings; these are the defaults. */
export const DOC_PREFIX = Object.freeze({
  [DOC_TYPE.INVOICE]: 'VJ',
  [DOC_TYPE.GOLD_PURCHASE]: 'GP',
  [DOC_TYPE.REPAIR]: 'RV',
  [DOC_TYPE.ISSUE]: 'ISS',
  [DOC_TYPE.RECEIPT]: 'RCT',
  [DOC_TYPE.CHIT_RECEIPT]: 'CHR',
  [DOC_TYPE.CHIT_ACCOUNT]: 'CHT',
});

/** Google Drive folder per document type. */
export const DRIVE_FOLDER = Object.freeze({
  [DOC_TYPE.INVOICE]: 'Invoices',
  [DOC_TYPE.GOLD_PURCHASE]: 'GoldPurchase',
  [DOC_TYPE.REPAIR]: 'RepairVouchers',
  [DOC_TYPE.ISSUE]: 'IssueVouchers',
  [DOC_TYPE.RECEIPT]: 'ReceiptVouchers',
  [DOC_TYPE.CHIT_RECEIPT]: 'ChitSystem',
  [DOC_TYPE.REPORT]: 'Reports',
  GST_INVOICE: 'GSTInvoices',
  BACKUP: 'DatabaseBackups',
});

export const METAL = Object.freeze({ GOLD: 'gold', SILVER: 'silver', PLATINUM: 'platinum' });

/**
 * HSN master, kept here as "Product name" - HSN Code so every module quotes
 * the same code for the same product line. Mirrors hsn_codes in the DB
 * (see backend/src/db/seeds/001_reference_data.js) — this file is the
 * default/fallback used before that table has loaded.
 *
 * "Gold and silver ornaments" - 7113 (3%)
 * "Gold Coin" - 7118 (3%)
 * "Gold Bar" - 7108 (3%)
 * "Silver bar" - 7106 (3%)
 * "Diamond stones" - 7102 (0.75%)
 * "Precious stones" - 7103 (0.125%)
 * "Articles of goldsmiths" - 7114 (3%)
 *
 * The stone codes' sub-1% rates are why hsn_codes.gst_rate and every rate
 * column that derives from it (invoice_items.gst_rate/cgst_rate/sgst_rate/
 * igst_rate) carry 3 decimal places, not 2 — see migration 024 and
 * shared/src/gst.js's formatRate(). A line can still override its HSN's rate
 * outright (invoiceItemSchema.gstRateOverride) for a one-off stone billed at
 * something other than the master rate.
 */
export const HSN_JEWELLERY = '7113';
export const HSN_GOLD_COIN = '7118';
export const HSN_GOLD_BAR = '7108';
export const HSN_SILVER_BAR = '7106';
export const HSN_DIAMOND_STONES = '7102';
export const HSN_PRECIOUS_STONES = '7103';
export const HSN_GOLDSMITH_ARTICLES = '7114';

/**
 * GST rate on jewellery. Making and stone charges are folded into the line's
 * taxable value and carry this same rate — a composite supply whose principal
 * supply is the jewellery. There is no separate 5% job-work line.
 */
export const GST_RATE_JEWELLERY = '3';

export const SUPPLY_TYPE = Object.freeze({ INTRA: 'intra', INTER: 'inter' });

/** Making charges are entered in rupees. Percentage entry is deliberately not offered. */
export const MAKING_CHARGE_TYPE = Object.freeze({
  FLAT: 'flat', // a rupee amount for the line
  PER_GRAM: 'per_gram', // rupees per gram, resolved to a rupee amount on entry
});

export const PAYMENT_MODE = Object.freeze({
  CASH: 'cash',
  UPI: 'upi',
  CARD: 'card',
  NETBANKING: 'netbanking',
  CHEQUE: 'cheque',
  OLD_GOLD: 'old_gold',
  CHIT_ADJUST: 'chit_adjust',
});

export const INVOICE_TYPE = Object.freeze({
  TAX_INVOICE: 'tax_invoice',
  BILL_OF_SUPPLY: 'bill_of_supply',
  ESTIMATE: 'estimate',
});

export const INVOICE_STATUS = Object.freeze({
  DRAFT: 'draft',
  ISSUED: 'issued',
  CANCELLED: 'cancelled',
});

export const PAYMENT_STATUS = Object.freeze({
  PAID: 'paid',
  PARTIAL: 'partial',
  UNPAID: 'unpaid',
});

/** IRP registration state for a B2B invoice. */
export const IRN_STATUS = Object.freeze({
  NOT_APPLICABLE: 'not_applicable', // B2C — no GSTIN on the bill
  PENDING: 'pending',
  REGISTERED: 'registered',
  FAILED: 'failed',
  CANCELLED: 'cancelled',
});

export const REPAIR_STATUS = Object.freeze({
  RECEIVED: 'received',
  IN_PROGRESS: 'in_progress',
  READY: 'ready',
  DELIVERED: 'delivered',
  CANCELLED: 'cancelled',
});

export const ISSUE_STATUS = Object.freeze({
  PENDING: 'pending',
  PARTIALLY_RETURNED: 'partially_returned',
  FULLY_RETURNED: 'fully_returned',
  CANCELLED: 'cancelled',
});

export const RECEIPT_ITEM_STATUS = Object.freeze({
  OK: 'ok',
  SHORT: 'short',
  DAMAGED: 'damaged',
  EXCESS: 'excess',
});

/** Both scheme types are supported; the type is fixed when the plan is created. */
export const CHIT_SCHEME_TYPE = Object.freeze({
  RUPEE: 'rupee', // pay a fixed amount monthly, bonus installment at maturity
  GOLD: 'gold', // each payment buys grams at that day's rate; grams accumulate
});

export const CHIT_ACCOUNT_STATUS = Object.freeze({
  ACTIVE: 'active',
  MATURED: 'matured',
  REDEEMED: 'redeemed',
  DEFAULTED: 'defaulted',
  CLOSED: 'closed',
});

export const CHIT_INSTALLMENT_STATUS = Object.freeze({
  PENDING: 'pending',
  PAID: 'paid',
  OVERDUE: 'overdue',
  WAIVED: 'waived',
});

/** Every weight change in the business writes one of these to stock_movements. */
export const MOVEMENT_TYPE = Object.freeze({
  PURCHASE: 'purchase',
  SALE: 'sale',
  ISSUE: 'issue',
  RECEIPT: 'receipt',
  REPAIR_IN: 'repair_in',
  REPAIR_OUT: 'repair_out',
  ADJUSTMENT: 'adjustment',
  MELT: 'melt',
});

export const PAPER_WIDTH = Object.freeze({ MM58: 58, MM80: 80, A4: 0 });

// Section 269ST (cash limit) lives in gst.js, alongside the exceeds269ST() check
// that uses it — see CASH_LIMIT_269ST there.

/**
 * Indian financial year for a date, as used in every document number.
 * 1 April 2026 -> "26-27"; 31 March 2026 -> "25-26".
 */
export function financialYear(date = new Date()) {
  const d = date instanceof Date ? date : new Date(date);
  const year = d.getFullYear();
  const startYear = d.getMonth() >= 3 ? year : year - 1; // month 3 === April
  return `${String(startYear % 100).padStart(2, '0')}-${String((startYear + 1) % 100).padStart(2, '0')}`;
}

/** First and last day of a financial year, for date-ranged reports and registers. */
export function financialYearRange(fy) {
  const [start] = fy.split('-').map((n) => 2000 + Number(n));
  return { from: `${start}-04-01`, to: `${start + 1}-03-31` };
}
